import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Section, Card, buttonStyles } from "@comai-portal/design-system";

/**
 * PayPage — the pay-a-quote page (CO 458).
 *
 * One offer, one price, one button. The seller (an agent) emails a link of the
 * form /pay/<tenant>~<product>; this page renders that one offer from the
 * public API and takes a card through Stripe.js. The amount never exists
 * client-side as an input — the server charges the catalog price or nothing.
 *
 * Card data lives entirely inside Stripe's iframe element; this page sees only
 * a pm_ token. 3DS: a requires_action reply carries the intent's client_secret
 * and stripe.handleNextAction runs the challenge; the order is then settled by
 * the webhook, so the page shows "processing" rather than pretending to know.
 */

interface Offer {
  offer: string;
  seller: string;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  available: boolean;
}

/** The slice of Stripe.js this page uses — typed locally, loaded dynamically. */
interface StripeCardElement {
  mount(sel: string | HTMLElement): void;
  unmount(): void;
}
interface StripeJs {
  elements(): { create(kind: "card", opts?: Record<string, unknown>): StripeCardElement };
  createPaymentMethod(opts: {
    type: "card";
    card: StripeCardElement;
    billing_details?: { email?: string };
  }): Promise<{ paymentMethod?: { id: string }; error?: { message?: string } }>;
  handleNextAction(opts: {
    clientSecret: string;
  }): Promise<{ paymentIntent?: { status?: string }; error?: { message?: string } }>;
}
declare global {
  interface Window {
    Stripe?: (pk: string) => StripeJs;
  }
}

function loadStripeJs(): Promise<void> {
  if (window.Stripe) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src^="https://js.stripe.com/v3"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("stripe.js failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://js.stripe.com/v3/";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("stripe.js failed to load"));
    document.head.appendChild(s);
  });
}

/**
 * CO 617 — the same shape bound the server applies (`EMAIL_SHAPE` in
 * public-checkout.ts). The server is the authority; this copy only saves the
 * buyer a round trip and keeps a card token from being minted for a request
 * that will be refused.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Phase =
  | { at: "loading" }
  | { at: "gone" } // offer 404 / unavailable / payments off
  | { at: "ready"; offer: Offer }
  | { at: "paying"; offer: Offer }
  | { at: "paid"; orderId: string; offer: Offer }
  | { at: "processing"; orderId: string; offer: Offer } // 3DS ran or secret missing; webhook settles
  | { at: "error"; offer: Offer; message: string };

export function PayPage() {
  const { offer: offerId } = useParams<{ offer: string }>();
  const [phase, setPhase] = useState<Phase>({ at: "loading" });
  const [email, setEmail] = useState("");
  const stripeRef = useRef<StripeJs | null>(null);
  const cardRef = useRef<StripeCardElement | null>(null);
  const cardHostRef = useRef<HTMLDivElement | null>(null);

  // Load the offer + Stripe config, then mount the card element.
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const [offerRes, cfgRes] = await Promise.all([
          fetch(`/pay/api/offer/${encodeURIComponent(offerId ?? "")}`),
          fetch("/pay/api/checkout-config"),
        ]);
        if (!offerRes.ok || !cfgRes.ok) {
          if (!dead) setPhase({ at: "gone" });
          return;
        }
        const offer = (await offerRes.json()) as Offer;
        const cfg = (await cfgRes.json()) as { publishableKey: string };
        if (!offer.available) {
          if (!dead) setPhase({ at: "gone" });
          return;
        }
        await loadStripeJs();
        if (dead || !window.Stripe) return;
        stripeRef.current = window.Stripe(cfg.publishableKey);
        setPhase({ at: "ready", offer });
      } catch {
        if (!dead) setPhase({ at: "gone" });
      }
    })();
    return () => {
      dead = true;
    };
  }, [offerId]);

  // Mount the card element once the host div renders.
  useEffect(() => {
    if (phase.at !== "ready" || !stripeRef.current || !cardHostRef.current || cardRef.current)
      return;
    const card = stripeRef.current.elements().create("card", {
      style: { base: { fontSize: "16px" } },
    });
    card.mount(cardHostRef.current);
    cardRef.current = card;
  }, [phase.at]);

  const pay = useCallback(async () => {
    if (phase.at !== "ready" && phase.at !== "error") return;
    const offer = phase.offer;
    const stripe = stripeRef.current;
    const card = cardRef.current;
    if (!stripe || !card) return;
    const buyerEmail = email.trim();
    if (!EMAIL_SHAPE.test(buyerEmail)) {
      setPhase({ at: "error", offer, message: "Enter your email address." });
      return;
    }
    setPhase({ at: "paying", offer });

    const pm = await stripe.createPaymentMethod({
      type: "card",
      card,
      billing_details: { email: buyerEmail },
    });
    if (!pm.paymentMethod) {
      setPhase({ at: "error", offer, message: pm.error?.message ?? "Card details look incomplete." });
      return;
    }

    const res = await fetch("/pay/api/checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        offer: offer.offer,
        payment_method: pm.paymentMethod.id,
        email: buyerEmail,
      }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      status?: string;
      order_id?: string;
      client_secret?: string;
      message?: string;
    };

    // CO 607-009 (review L2) — a paid reply always draws the paid card; a missing id hides
    // only the order sentence, never falls through to "No charge was made".
    if (res.ok && body.status === "paid") {
      setPhase({ at: "paid", orderId: body.order_id ?? "", offer });
      return;
    }
    if (res.ok && body.status === "requires_action") {
      if (body.client_secret) {
        const action = await stripe.handleNextAction({ clientSecret: body.client_secret });
        if (action.error) {
          setPhase({
            at: "error",
            offer,
            message: action.error.message ?? "Verification did not complete. You have not been charged.",
          });
          return;
        }
      }
      // The webhook settles the order; the page does not pretend to know first.
      setPhase({ at: "processing", orderId: body.order_id ?? "", offer });
      return;
    }
    setPhase({
      at: "error",
      offer,
      message: body.message ?? "Checkout failed. No charge was made.",
    });
  }, [phase, email]);

  const offer = "offer" in phase ? phase.offer : null;

  return (
    <Section>
      <Container width="prose">
        {phase.at === "loading" && <p>Loading offer…</p>}

        {phase.at === "gone" && (
          <Card>
            <h1 style={{ marginTop: 0 }}>Offer unavailable</h1>
            <p>
              This offer doesn't exist, has already been purchased, or payments are
              temporarily off. If someone sent you this link, reply to them — they can
              issue a fresh one.
            </p>
          </Card>
        )}

        {phase.at === "paid" && offer && (
          <Card>
            <h1 style={{ marginTop: 0 }}>Paid — thank you</h1>
            <p>
              <strong>{offer.name}</strong> · ${offer.price.toFixed(2)} USD
            </p>
            {phase.orderId && (
              <p>
                Order <code>{phase.orderId}</code> is confirmed. Keep this order number: {offer.seller} has the same order.
              </p>
            )}
          </Card>
        )}

        {phase.at === "processing" && offer && (
          <Card>
            <h1 style={{ marginTop: 0 }}>Payment processing</h1>
            <p>
              Your bank is confirming the payment. Nothing more is needed from you here.
            </p>
            {phase.orderId && (
              <p>
                Your order number is <code>{phase.orderId}</code>. {offer.seller} sees it once the payment settles.
              </p>
            )}
          </Card>
        )}

        {(phase.at === "ready" || phase.at === "paying" || phase.at === "error") && offer && (
          <Card>
            <p style={{ margin: 0, opacity: 0.7 }}>{offer.seller}</p>
            <h1 style={{ marginTop: 4 }}>{offer.name}</h1>
            {offer.description && <p>{offer.description}</p>}
            <p style={{ fontSize: "1.5rem", fontWeight: 700 }}>
              ${offer.price.toFixed(2)} <span style={{ fontSize: "0.9rem" }}>USD</span>
            </p>

            <label style={{ display: "block", marginBottom: 12 }}>
              Your email
              <input
                type="email"
                required
                autoComplete="email"
                aria-describedby="pay-email-hint"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 4,
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(128,128,128,0.4)",
                  background: "transparent",
                  color: "inherit",
                  font: "inherit",
                }}
              />
            </label>
            <span id="pay-email-hint" style={{ display: "block", marginTop: -8, marginBottom: 12, fontSize: "0.8rem", opacity: 0.7 }}>
              The seller sees this with your order.
            </span>

            <div
              ref={cardHostRef}
              style={{
                padding: "12px",
                borderRadius: 8,
                border: "1px solid rgba(128,128,128,0.4)",
                marginBottom: 12,
              }}
            />

            {phase.at === "error" && (
              <p role="alert" style={{ color: "#c0392b" }}>
                {phase.message}
              </p>
            )}

            <button
              type="button"
              onClick={() => void pay()}
              disabled={phase.at === "paying"}
              className={buttonStyles({ variant: "primary" })}
            >
              {phase.at === "paying" ? "Charging…" : `Pay $${offer.price.toFixed(2)}`}
            </button>

            <p style={{ fontSize: "0.8rem", opacity: 0.7, marginTop: 12 }}>
              You are not charged until you press Pay. Card handled by Stripe — the card number never
              touches our servers.
            </p>
          </Card>
        )}
      </Container>
    </Section>
  );
}
